import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Collapse, MenuItem, TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { alpha } from "@mui/material/styles";
import {
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
    CheckCircleRounded,
    InfoOutlined,
    InfoRounded,
    PendingActionsRounded,
} from "@mui/icons-material";
import { OUTLINED_INPUT_SX } from "../common/outlinedInputStyles";
import Tooltip from "../common/Tooltip";
import CountrySelect, {
    COUNTRY_SELECT_POPUP_ICON,
} from "../common/CountrySelect";
import {
    COUNTRY_SELECT_FIELD_SX,
    COUNTRY_SELECT_LISTBOX_SX,
    COUNTRY_SELECT_PAPER_SX,
} from "../common/countrySelectStyles";
import { useCountriesList } from "../../hooks/useCountriesList";
import { findCountryMatch, getCountryDisplayName } from "../../utils/countryUtils";
import { normalizeExternalProfileUrl } from "../../utils/externalProfileUrl";
import { fetchSteamChecks } from "../../redux/actions/catalogActions";
import { toast } from "../../universal/toast";
import {
    calculateAgeFromDob,
    formatDateOfBirthDisplay,
    formatDateOfBirthInputValue,
    getAccountLegalName,
    getAccountNameParts,
    getNicknameValidationError,
    maskDateOfBirthInput,
    NICKNAME_HINT,
    NICKNAME_MAX_LENGTH,
    parseDateOfBirthInputValue,
} from "./accountFormUtils";
import AccountIdentityVerification from "./AccountIdentityVerification";
import AccountPhoneField from "./AccountPhoneField";
import AccountDataDeletionSection from "./AccountDataDeletionSection.jsx";
import AccountProfileSummary from "./AccountProfileSummary";
import AccountProfileReadiness from "./AccountProfileReadiness";
import {
    ACCOUNT_PAGE_COUNTRY_SELECT_SX,
    ACCOUNT_PAGE_DELETION_CONTENT_SX,
    ACCOUNT_PAGE_DELETION_ROW_SX,
    ACCOUNT_PAGE_DELETION_TITLE_SX,
    ACCOUNT_PAGE_FIELD_ACTIONS_SX,
    ACCOUNT_PAGE_FIELD_CANCEL_BUTTON_SX,
    ACCOUNT_PAGE_FIELD_DIVIDER_SX,
    ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX,
    ACCOUNT_PAGE_FIELD_INPUT_WRAP_SX,
    ACCOUNT_PAGE_FIELD_INPUT_WRAP_WIDE_SX,
    ACCOUNT_PAGE_FIELD_LABEL_SX,
    ACCOUNT_PAGE_FIELD_ROW_SX,
    ACCOUNT_PAGE_FIELD_SAVE_BUTTON_SX,
    ACCOUNT_PAGE_INLINE_FIELD_SX,
    ACCOUNT_PAGE_INLINE_FIELDS_SX,
    ACCOUNT_PAGE_KYC_STATUS_CHIP_SX,
    ACCOUNT_PAGE_MAIN_CONTENT_SX,
    ACCOUNT_PAGE_MATCHMAKING_NOTICE_SX,
    ACCOUNT_PAGE_OVERVIEW_STACK_SX,
    ACCOUNT_PAGE_REVIEW_NOTICE_SX,
    ACCOUNT_PAGE_SECTION_HEADER_SX,
    ACCOUNT_PAGE_SECTION_SX,
    ACCOUNT_PAGE_TITLE_SX,
    ACCOUNT_PAGE_SUBSECTION_ARROW_SX,
    ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX,
    ACCOUNT_PAGE_SUBSECTION_SX,
    ACCOUNT_PAGE_SUBSECTION_TITLE_SX,
    ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX,
    ACCOUNT_PAGE_TWO_FACTOR_STATUS_SX,
} from "./accountPageStyles";
import { colors } from "../../theme/colors";

const SUBSECTION_COLLAPSE_TIMEOUT = {
    enter: 320,
    exit: 260,
};

const SUBSECTION_COLLAPSE_EASING = {
    enter: "cubic-bezier(0.22, 1, 0.36, 1)",
    exit: "cubic-bezier(0.4, 0, 0.2, 1)",
};

const EMPTY_ACCOUNT_FIELD_DISPLAY = "Not set";

function formatGenderDisplay(value) {
    if (value === "male") return "Male";
    if (value === "female") return "Female";
    return EMPTY_ACCOUNT_FIELD_DISPLAY;
}

const GENDER_OPTIONS = [
    { value: "male", displayName: "Male" },
    { value: "female", displayName: "Female" },
];

const STEAM_INVENTORY_OPTIONS = [
    { value: "none", displayName: "None" },
    { value: "have", displayName: "I have an inventory" },
    {
        value: "have_considerable",
        displayName: "I have a considerable inventory (above $500)",
    },
];

function getStringFieldValue(value) {
    return typeof value === "string" ? value.trim() : "";
}

function getAccountFieldDisplayValue(value) {
    return getStringFieldValue(value) || EMPTY_ACCOUNT_FIELD_DISPLAY;
}

function getSteamId64Value(user) {
    return getStringFieldValue(user?.steamId64 || user?.steam64Id);
}

function getSteamProfileUrlValue(user) {
    return getStringFieldValue(
        user?.steamProfileUrl || user?.steamProvider?.profileUrl,
    );
}

function formatSteamInventoryDisplay(value) {
    const selected = STEAM_INVENTORY_OPTIONS.find(
        (option) => option.value === value,
    );
    return selected?.displayName || "";
}

function SteamInventorySelect({ value, onChange }) {
    const selectedOption =
        STEAM_INVENTORY_OPTIONS.find((option) => option.value === value) || null;

    return (
        <Autocomplete
            disableClearable
            size="small"
            id="account-steam-inventory"
            sx={[COUNTRY_SELECT_FIELD_SX, ACCOUNT_PAGE_COUNTRY_SELECT_SX]}
            options={STEAM_INVENTORY_OPTIONS}
            value={selectedOption}
            onChange={(_event, nextOption) => {
                onChange(nextOption?.value || "none");
            }}
            popupIcon={COUNTRY_SELECT_POPUP_ICON}
            slotProps={{
                paper: {
                    sx: COUNTRY_SELECT_PAPER_SX,
                },
                listbox: {
                    onWheel: (event) => {
                        event.stopPropagation();
                    },
                    sx: COUNTRY_SELECT_LISTBOX_SX,
                },
            }}
            getOptionLabel={(option) => option?.displayName || ""}
            isOptionEqualToValue={(option, selected) =>
                option?.value === selected?.value
            }
            renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                    <Box component="li" key={key} {...optionProps}>
                        <Box
                            sx={{
                                display: "flex",
                                flex: 1,
                                minWidth: 0,
                                width: "100%",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "0.75rem",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {option.displayName}
                            </Box>
                            {selected ? (
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        flexShrink: 0,
                                        fontSize: "0.85rem",
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        color: colors.accent.soft,
                                    }}
                                >
                                    ✓
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Select inventory"
                    slotProps={{
                        htmlInput: {
                            ...params.inputProps,
                            "aria-label": "Steam CS Inventory",
                            readOnly: true,
                        },
                        input: {
                            ...params.InputProps,
                        },
                    }}
                />
            )}
        />
    );
}

SteamInventorySelect.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};

function AccountLabeledInput({
    id,
    label,
    value,
    onChange,
    autoFocus = false,
    placeholder,
    inputMode,
    maxLength,
}) {
    return (
        <Box sx={ACCOUNT_PAGE_INLINE_FIELD_SX}>
            <Typography
                component="label"
                htmlFor={id}
                sx={ACCOUNT_PAGE_FIELD_LABEL_SX}
            >
                {label}
            </Typography>
            <TextField
                id={id}
                variant="outlined"
                value={value}
                onChange={onChange}
                autoFocus={autoFocus}
                placeholder={placeholder}
                sx={OUTLINED_INPUT_SX}
                slotProps={{
                    htmlInput: {
                        "aria-label": label,
                        autoComplete: "off",
                        autoCorrect: "off",
                        spellCheck: false,
                        inputMode,
                        maxLength,
                    },
                }}
            />
        </Box>
    );
}

AccountLabeledInput.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    autoFocus: PropTypes.bool,
    placeholder: PropTypes.string,
    inputMode: PropTypes.string,
    maxLength: PropTypes.number,
};

function AccountInfoField({
    id,
    label,
    labelAdornment = null,
    value,
    editable = true,
    editing = false,
    draftValue = "",
    onDraftChange,
    onEdit,
    onCancel,
    onSave,
    saving = false,
    editor = null,
    hideLabel = false,
    wrapSx = ACCOUNT_PAGE_FIELD_INPUT_WRAP_SX,
    hint = "",
    error = "",
    saveDisabled = false,
    inputMaxLength,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.45rem",
                width: "100%",
            }}
        >
            {hideLabel ? null : (
                <Typography
                    component="label"
                    htmlFor={id}
                    sx={ACCOUNT_PAGE_FIELD_LABEL_SX}
                >
                    {labelAdornment ? (
                        <Box
                            component="span"
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.4rem",
                            }}
                        >
                            {label}
                            {labelAdornment}
                        </Box>
                    ) : (
                        label
                    )}
                </Typography>
            )}
            <Box sx={ACCOUNT_PAGE_FIELD_ROW_SX}>
                <Box sx={wrapSx}>
                    {editor && editing ? (
                        editor
                    ) : (
                        <TextField
                            id={id}
                            variant="outlined"
                            value={
                                editing
                                    ? draftValue
                                    : getAccountFieldDisplayValue(value)
                            }
                            disabled={!editing}
                            onChange={
                                editing && onDraftChange
                                    ? (event) => onDraftChange(event.target.value)
                                    : undefined
                            }
                            autoFocus={editing}
                            sx={OUTLINED_INPUT_SX}
                            slotProps={{
                                input: {
                                    readOnly: !editing,
                                },
                                htmlInput: {
                                    "aria-label": label,
                                    readOnly: !editing,
                                    autoComplete: "off",
                                    autoCorrect: "off",
                                    spellCheck: false,
                                    maxLength: inputMaxLength,
                                },
                            }}
                        />
                    )}
                </Box>
                {editable && !editing ? (
                    <Box
                        component="button"
                        type="button"
                        aria-label={`Edit ${label.toLowerCase()}`}
                        onClick={onEdit}
                        sx={ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX}
                    >
                        Edit
                    </Box>
                ) : null}
            </Box>
            {editable ? (
                <Collapse in={editing} timeout={180}>
                    {hint ? (
                        <Typography
                            sx={{
                                mt: "0.4rem",
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                letterSpacing: "0.01em",
                                color: colors.text.muted,
                                lineHeight: 1.4,
                            }}
                        >
                            {hint}
                        </Typography>
                    ) : null}
                    {error ? (
                        <Typography
                            sx={{
                                mt: hint ? "0.18rem" : "0.4rem",
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: colors.state.danger.main,
                                lineHeight: 1.35,
                            }}
                        >
                            {error}
                        </Typography>
                    ) : null}
                    <Box sx={ACCOUNT_PAGE_FIELD_ACTIONS_SX}>
                        <Box
                            component="button"
                            type="button"
                            disabled={saving || saveDisabled}
                            onClick={onSave}
                            sx={{
                                ...ACCOUNT_PAGE_FIELD_SAVE_BUTTON_SX,
                                "&:disabled": {
                                    opacity: 0.45,
                                    cursor: "not-allowed",
                                    background: colors.primary.hover,
                                },
                            }}
                        >
                            {saving ? "Saving" : "Save"}
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            disabled={saving}
                            onClick={onCancel}
                            sx={ACCOUNT_PAGE_FIELD_CANCEL_BUTTON_SX}
                        >
                            Cancel
                        </Box>
                    </Box>
                </Collapse>
            ) : null}
        </Box>
    );
}

AccountInfoField.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    labelAdornment: PropTypes.node,
    value: PropTypes.string,
    editable: PropTypes.bool,
    editing: PropTypes.bool,
    draftValue: PropTypes.string,
    onDraftChange: PropTypes.func,
    onEdit: PropTypes.func,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
    saving: PropTypes.bool,
    editor: PropTypes.node,
    hideLabel: PropTypes.bool,
    wrapSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    hint: PropTypes.string,
    error: PropTypes.string,
    saveDisabled: PropTypes.bool,
    inputMaxLength: PropTypes.number,
};

function AccountTwoFactorRow({ enabled, onEdit }) {
    return (
        <Box sx={ACCOUNT_PAGE_DELETION_ROW_SX}>
            <Box sx={ACCOUNT_PAGE_DELETION_CONTENT_SX}>
                <Typography component="h3" sx={ACCOUNT_PAGE_DELETION_TITLE_SX}>
                    Two-factor Authentication
                </Typography>
                {enabled ? (
                    <Box
                        component="span"
                        sx={[
                            ACCOUNT_PAGE_KYC_STATUS_CHIP_SX,
                            { alignSelf: "flex-start", width: "fit-content" },
                        ]}
                    >
                        Activated
                        <CheckCircleRounded sx={{ fontSize: "0.95rem" }} />
                    </Box>
                ) : (
                    <Typography
                        sx={{
                            ...ACCOUNT_PAGE_TWO_FACTOR_STATUS_SX,
                            color: colors.text.muted,
                        }}
                    >
                        Disabled
                    </Typography>
                )}
            </Box>
            <Box
                component="button"
                type="button"
                aria-label="Edit two-factor authentication"
                onClick={onEdit}
                sx={ACCOUNT_PAGE_FIELD_EDIT_BUTTON_SX}
            >
                Edit
            </Box>
        </Box>
    );
}

AccountTwoFactorRow.propTypes = {
    enabled: PropTypes.bool,
    onEdit: PropTypes.func,
};

function AccountCollapsibleSubsection({
    id,
    title,
    children,
    sx,
    showDivider = false,
}) {
    const [open, setOpen] = useState(true);
    const contentId = `${id}-content`;
    const ArrowIcon = open ? KeyboardArrowUpRounded : KeyboardArrowDownRounded;

    return (
        <Box sx={sx}>
            <Typography component="h2" sx={{ m: 0, p: 0 }}>
                <Box
                    component="button"
                    type="button"
                    aria-expanded={open}
                    aria-controls={contentId}
                    onClick={() => setOpen((current) => !current)}
                    sx={ACCOUNT_PAGE_SUBSECTION_TOGGLE_SX}
                >
                    <Box component="span" sx={ACCOUNT_PAGE_SUBSECTION_TITLE_SX}>
                        {title}
                    </Box>
                    <ArrowIcon aria-hidden sx={ACCOUNT_PAGE_SUBSECTION_ARROW_SX} />
                </Box>
            </Typography>
            <Collapse
                in={open}
                timeout={SUBSECTION_COLLAPSE_TIMEOUT}
                easing={SUBSECTION_COLLAPSE_EASING}
            >
                <Box id={contentId}>{children}</Box>
            </Collapse>
            {showDivider ? <Box sx={ACCOUNT_PAGE_SUBSECTION_DIVIDER_SX} /> : null}
        </Box>
    );
}

AccountCollapsibleSubsection.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.node,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    showDivider: PropTypes.bool,
};

function AccountTabSection({ user, actions, refreshToken, onSectionChange }) {
    const savedNickname = user?.nickname || user?.name || "";
    const savedNameParts = getAccountNameParts(user);
    const [email, setEmail] = useState(user?.email || "");
    const [legalName, setLegalName] = useState(getAccountLegalName(user));
    const [dateOfBirth, setDateOfBirth] = useState(
        formatDateOfBirthDisplay(user?.dateOfBirth),
    );
    const [gender, setGender] = useState(formatGenderDisplay(user?.gender));
    const [readinessComplete, setReadinessComplete] = useState(false);
    const [editingNickname, setEditingNickname] = useState(false);
    const [draftNickname, setDraftNickname] = useState("");
    const [savingNickname, setSavingNickname] = useState(false);
    const [editingCountry, setEditingCountry] = useState(false);
    const [draftCountry, setDraftCountry] = useState(null);
    const [savingCountry, setSavingCountry] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [draftGivenName, setDraftGivenName] = useState("");
    const [draftFamilyName, setDraftFamilyName] = useState("");
    const [savingName, setSavingName] = useState(false);
    const [editingDob, setEditingDob] = useState(false);
    const [draftDob, setDraftDob] = useState("");
    const [savingDob, setSavingDob] = useState(false);
    const [editingGender, setEditingGender] = useState(false);
    const [draftGender, setDraftGender] = useState("");
    const [savingGender, setSavingGender] = useState(false);
    const [editingSteamId64, setEditingSteamId64] = useState(false);
    const [draftSteamId64, setDraftSteamId64] = useState("");
    const [savingSteamId64, setSavingSteamId64] = useState(false);
    const [steamId64Locked, setSteamId64Locked] = useState(
        Boolean(getSteamId64Value(user)),
    );
    const [editingSteamProfileUrl, setEditingSteamProfileUrl] = useState(false);
    const [draftSteamProfileUrl, setDraftSteamProfileUrl] = useState("");
    const [savingSteamProfileUrl, setSavingSteamProfileUrl] = useState(false);
    const [steamProfileUrlLocked, setSteamProfileUrlLocked] = useState(
        Boolean(getSteamProfileUrlValue(user)),
    );
    const [editingSteamInventory, setEditingSteamInventory] = useState(false);
    const [draftSteamInventoryValue, setDraftSteamInventoryValue] = useState("");
    const [savingSteamInventory, setSavingSteamInventory] = useState(false);
    const [editingFaceitProfileUrl, setEditingFaceitProfileUrl] = useState(false);
    const [draftFaceitProfileUrl, setDraftFaceitProfileUrl] = useState("");
    const [savingFaceitProfileUrl, setSavingFaceitProfileUrl] = useState(false);
    const [faceitProfileUrlLocked, setFaceitProfileUrlLocked] = useState(
        Boolean(getStringFieldValue(user?.faceitProfileUrl)),
    );
    const { countries, loading: countriesLoading } = useCountriesList({
        enabled: Boolean(user?.userId),
    });
    const savedCountryName = getCountryDisplayName(countries, user?.country);
    const savedSteamId64 = getSteamId64Value(user);
    const savedSteamProfileUrl = getSteamProfileUrlValue(user);
    const savedSteamInventoryValue = getStringFieldValue(user?.steamInventoryValue);
    const savedSteamInventoryDisplay = formatSteamInventoryDisplay(
        savedSteamInventoryValue,
    );
    const savedFaceitProfileUrl = getStringFieldValue(user?.faceitProfileUrl);
    const canEditSteamId64 = !steamId64Locked && !savedSteamId64;
    const canEditSteamProfileUrl =
        !steamProfileUrlLocked && !savedSteamProfileUrl;
    const canEditFaceitProfileUrl =
        !faceitProfileUrlLocked && !savedFaceitProfileUrl;
    const showPendingReviewNotice =
        user?.accountStatus === "pending_review" && readinessComplete;

    useEffect(() => {
        setEmail(user?.email || "");
    }, [user?.email]);

    useEffect(() => {
        setLegalName(getAccountLegalName(user));
    }, [user?.given_name, user?.family_name, user?.firstName, user?.lastName, user?.name]);

    useEffect(() => {
        setDateOfBirth(formatDateOfBirthDisplay(user?.dateOfBirth));
    }, [user?.dateOfBirth]);

    useEffect(() => {
        setGender(formatGenderDisplay(user?.gender));
    }, [user?.gender]);

    useEffect(() => {
        setSteamId64Locked(Boolean(savedSteamId64));
    }, [savedSteamId64, user?.userId]);

    useEffect(() => {
        setSteamProfileUrlLocked(Boolean(savedSteamProfileUrl));
    }, [savedSteamProfileUrl, user?.userId]);

    useEffect(() => {
        setFaceitProfileUrlLocked(Boolean(savedFaceitProfileUrl));
    }, [savedFaceitProfileUrl, user?.userId]);

    const resetEditors = useCallback(() => {
        setEditingNickname(false);
        setDraftNickname("");
        setEditingCountry(false);
        setDraftCountry(null);
        setEditingName(false);
        setDraftGivenName("");
        setDraftFamilyName("");
        setEditingDob(false);
        setDraftDob("");
        setEditingGender(false);
        setDraftGender("");
        setEditingSteamId64(false);
        setDraftSteamId64("");
        setEditingSteamProfileUrl(false);
        setDraftSteamProfileUrl("");
        setEditingSteamInventory(false);
        setDraftSteamInventoryValue("");
        setEditingFaceitProfileUrl(false);
        setDraftFaceitProfileUrl("");
    }, []);

    const saveAccountField = useCallback(
        async (payload) => {
            if (typeof actions?.submitAccountChanges !== "function") {
                toast.error("Unable to save account changes.");
                return false;
            }

            const result = await actions.submitAccountChanges({
                userId: user?.userId,
                ...payload,
            });

            if (result?.type !== "ACCOUNT_UPDATE_COMPLETE") {
                toast.error(result?.text || "Failed to save account changes.");
                return false;
            }

            return true;
        },
        [actions, user?.userId],
    );

    const nicknameDraftError = editingNickname
        ? getNicknameValidationError(draftNickname)
        : null;

    const handleSaveNickname = useCallback(async () => {
        const nextNickname = draftNickname.trim();
        if (!nextNickname || savingNickname) return;
        const nicknameError = getNicknameValidationError(nextNickname);
        if (nicknameError) {
            toast.error(nicknameError);
            return;
        }

        setSavingNickname(true);
        const saved = await saveAccountField({ nickname: nextNickname });
        setSavingNickname(false);
        if (!saved) return;

        setEditingNickname(false);
        setDraftNickname("");
        toast.success("Nickname saved successfully");
    }, [draftNickname, saveAccountField, savingNickname]);

    const handleSaveCountry = useCallback(async () => {
        const nextCountry = draftCountry?.displayName || "";
        if (!nextCountry || savingCountry) return;

        setSavingCountry(true);
        const saved = await saveAccountField({ country: nextCountry });
        setSavingCountry(false);
        if (!saved) return;

        setEditingCountry(false);
        setDraftCountry(null);
        toast.success("Country saved successfully");
    }, [draftCountry, saveAccountField, savingCountry]);

    const handleSaveName = useCallback(async () => {
        const givenName = draftGivenName.trim();
        const familyName = draftFamilyName.trim();
        if (savingName) return;
        if (!givenName) {
            toast.error("Enter your name.");
            return;
        }

        setSavingName(true);
        const saved = await saveAccountField({
            given_name: givenName,
            family_name: familyName,
            name: `${givenName} ${familyName}`.trim(),
        });
        setSavingName(false);
        if (!saved) return;

        setEditingName(false);
        setDraftGivenName("");
        setDraftFamilyName("");
        toast.success("Name saved successfully");
    }, [draftFamilyName, draftGivenName, saveAccountField, savingName]);

    const handleSaveDob = useCallback(async () => {
        if (savingDob) return;

        const nextDob = parseDateOfBirthInputValue(draftDob);
        const age = calculateAgeFromDob(nextDob);
        if (!nextDob || age === null || age < 13 || age > 90) {
            toast.error("Enter a valid date of birth as MM/DD/YYYY.");
            return;
        }

        setSavingDob(true);
        const saved = await saveAccountField({ dateOfBirth: nextDob });
        setSavingDob(false);
        if (!saved) return;

        setEditingDob(false);
        setDraftDob("");
        toast.success("Date of birth saved successfully");
    }, [draftDob, saveAccountField, savingDob]);

    const handleSaveGender = useCallback(async () => {
        if (savingGender) return;
        if (draftGender !== "male" && draftGender !== "female") {
            toast.error("Select your gender.");
            return;
        }

        setSavingGender(true);
        const saved = await saveAccountField({ gender: draftGender });
        setSavingGender(false);
        if (!saved) return;

        setEditingGender(false);
        setDraftGender("");
        toast.success("Gender saved successfully");
    }, [draftGender, saveAccountField, savingGender]);

    const handleSaveSteamId64 = useCallback(async () => {
        const nextSteamId64 = draftSteamId64.trim();
        if (savingSteamId64) return;
        if (!nextSteamId64) {
            toast.error("You didn't insert your SteamID 64 from your Steam account page");
            return;
        }
        if (!/^\d{15,20}$/.test(nextSteamId64)) {
            toast.error("SteamID 64 must contain 15 to 20 digits.");
            return;
        }

        setSavingSteamId64(true);
        try {
            try {
                const steamCheckResult = await fetchSteamChecks(nextSteamId64);
                if (steamCheckResult?.checks?.inUse) {
                    toast.error(
                        "This Steam ID is already linked to another Ouro account.",
                    );
                    return;
                }
            } catch (_) {
                // The account API performs the authoritative duplicate check.
            }

            const saved = await saveAccountField({ steamId64: nextSteamId64 });
            if (!saved) return;

            setSteamId64Locked(true);
            setEditingSteamId64(false);
            setDraftSteamId64("");
            toast.success("SteamID 64 saved successfully");
        } finally {
            setSavingSteamId64(false);
        }
    }, [draftSteamId64, saveAccountField, savingSteamId64]);

    const handleSaveSteamProfileUrl = useCallback(async () => {
        const nextSteamProfileUrl = draftSteamProfileUrl.trim();
        if (savingSteamProfileUrl) return;
        if (!nextSteamProfileUrl) {
            toast.error("Missing Steam profile URL");
            return;
        }

        const normalizedSteamProfileUrl = normalizeExternalProfileUrl(
            nextSteamProfileUrl,
        );
        if (!normalizedSteamProfileUrl) {
            toast.error("Steam profile URL must be a valid http or https URL");
            return;
        }

        setSavingSteamProfileUrl(true);
        const saved = await saveAccountField({
            steamProfileUrl: normalizedSteamProfileUrl,
        });
        setSavingSteamProfileUrl(false);
        if (!saved) return;

        setSteamProfileUrlLocked(true);
        setEditingSteamProfileUrl(false);
        setDraftSteamProfileUrl("");
        toast.success("Steam profile URL saved successfully");
    }, [draftSteamProfileUrl, saveAccountField, savingSteamProfileUrl]);

    const handleSaveSteamInventory = useCallback(async () => {
        if (savingSteamInventory) return;
        const nextSteamInventoryValue = draftSteamInventoryValue.trim();
        const isKnownInventoryValue = STEAM_INVENTORY_OPTIONS.some(
            (option) => option.value === nextSteamInventoryValue,
        );
        if (!isKnownInventoryValue) {
            toast.error("Select your Steam CS inventory.");
            return;
        }

        setSavingSteamInventory(true);
        const saved = await saveAccountField({
            steamInventoryValue: nextSteamInventoryValue,
        });
        setSavingSteamInventory(false);
        if (!saved) return;

        setEditingSteamInventory(false);
        setDraftSteamInventoryValue("");
        toast.success("Steam CS inventory saved successfully");
    }, [draftSteamInventoryValue, saveAccountField, savingSteamInventory]);

    const handleSaveFaceitProfileUrl = useCallback(async () => {
        const nextFaceitProfileUrl = draftFaceitProfileUrl.trim();
        if (savingFaceitProfileUrl) return;
        if (!nextFaceitProfileUrl) {
            toast.error("Enter a FACEIT profile URL or cancel.");
            return;
        }

        const normalizedFaceitProfileUrl = normalizeExternalProfileUrl(
            nextFaceitProfileUrl,
        );
        if (!normalizedFaceitProfileUrl) {
            toast.error("FACEIT profile URL must be a valid http or https URL.");
            return;
        }

        setSavingFaceitProfileUrl(true);
        const saved = await saveAccountField({
            faceitProfileUrl: normalizedFaceitProfileUrl,
        });
        setSavingFaceitProfileUrl(false);
        if (!saved) return;

        setFaceitProfileUrlLocked(true);
        setEditingFaceitProfileUrl(false);
        setDraftFaceitProfileUrl("");
        toast.success("FACEIT profile URL saved successfully");
    }, [draftFaceitProfileUrl, saveAccountField, savingFaceitProfileUrl]);

    return (
        <Box sx={ACCOUNT_PAGE_MAIN_CONTENT_SX}>
            <Box sx={ACCOUNT_PAGE_SECTION_SX}>
                <Box sx={ACCOUNT_PAGE_SECTION_HEADER_SX}>
                    <Typography
                        component="h1"
                        sx={ACCOUNT_PAGE_TITLE_SX}
                    >
                        Account
                    </Typography>
                </Box>

                <Box sx={ACCOUNT_PAGE_OVERVIEW_STACK_SX}>
                    <AccountProfileReadiness
                        user={user}
                        refreshToken={refreshToken}
                        onReadinessChange={setReadinessComplete}
                    />
                    {showPendingReviewNotice ? (
                        <Box
                            role="status"
                            aria-live="polite"
                            sx={ACCOUNT_PAGE_REVIEW_NOTICE_SX}
                        >
                            <Box
                                className="account-review-notice-icon"
                                aria-hidden
                            >
                                <PendingActionsRounded />
                            </Box>
                            <Box
                                component="span"
                                className="account-review-notice-text"
                            >
                                Your profile is complete and under review
                            </Box>
                        </Box>
                    ) : null}
                    <AccountProfileSummary user={user} />
                </Box>

                <AccountCollapsibleSubsection
                    id="account-information"
                    title="Account information"
                    sx={ACCOUNT_PAGE_SUBSECTION_SX}
                    showDivider
                >
                    <Box sx={{ mt: "1.25rem" }}>
                        <AccountInfoField
                            id="account-nickname"
                            label="Nickname"
                            value={savedNickname}
                            editing={editingNickname}
                            draftValue={draftNickname}
                            onDraftChange={setDraftNickname}
                            onEdit={() => {
                                resetEditors();
                                setDraftNickname(savedNickname);
                                setEditingNickname(true);
                            }}
                            onCancel={() => {
                                setEditingNickname(false);
                                setDraftNickname("");
                            }}
                            onSave={handleSaveNickname}
                            saving={savingNickname}
                            hint={NICKNAME_HINT}
                            error={nicknameDraftError || ""}
                            saveDisabled={Boolean(nicknameDraftError)}
                            inputMaxLength={NICKNAME_MAX_LENGTH}
                        />
                    </Box>

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountInfoField
                        id="account-email"
                        label="Email"
                        value={email}
                        editable={false}
                    />

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountInfoField
                        id="account-country"
                        label="Country"
                        value={savedCountryName}
                        editing={editingCountry}
                        onEdit={() => {
                            resetEditors();
                            setDraftCountry(findCountryMatch(countries, user?.country));
                            setEditingCountry(true);
                        }}
                        onCancel={() => {
                            setEditingCountry(false);
                            setDraftCountry(null);
                        }}
                        onSave={handleSaveCountry}
                        saving={savingCountry}
                        editor={
                            <CountrySelect
                                id="account-country"
                                countries={countries}
                                loading={countriesLoading}
                                value={draftCountry}
                                onChange={(_event, nextValue) => {
                                    setDraftCountry(nextValue || null);
                                }}
                                placeholder="Type to search (e.g. ge → Germany)"
                                inputAriaLabel="Country"
                                sx={ACCOUNT_PAGE_COUNTRY_SELECT_SX}
                            />
                        }
                    />
                </AccountCollapsibleSubsection>

                <AccountCollapsibleSubsection
                    id="account-matchmaking-details"
                    title="Matchmaking details"
                    showDivider
                >
                    <Box sx={{ mt: "1.25rem" }}>
                        <AccountInfoField
                            id="account-steam64-id"
                            label="SteamID 64"
                            value={savedSteamId64}
                            editable={canEditSteamId64}
                            editing={editingSteamId64}
                            draftValue={draftSteamId64}
                            onDraftChange={(value) => {
                                setDraftSteamId64(value.replace(/\D/g, ""));
                            }}
                            onEdit={() => {
                                resetEditors();
                                setDraftSteamId64(savedSteamId64);
                                setEditingSteamId64(true);
                            }}
                            onCancel={() => {
                                setEditingSteamId64(false);
                                setDraftSteamId64("");
                            }}
                            onSave={handleSaveSteamId64}
                            saving={savingSteamId64}
                        />
                    </Box>

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountInfoField
                        id="account-steam-profile-url"
                        label="Steam Profile URL"
                        value={savedSteamProfileUrl}
                        editable={canEditSteamProfileUrl}
                        editing={editingSteamProfileUrl}
                        draftValue={draftSteamProfileUrl}
                        onDraftChange={setDraftSteamProfileUrl}
                        onEdit={() => {
                            resetEditors();
                            setDraftSteamProfileUrl(savedSteamProfileUrl);
                            setEditingSteamProfileUrl(true);
                        }}
                        onCancel={() => {
                            setEditingSteamProfileUrl(false);
                            setDraftSteamProfileUrl("");
                        }}
                        onSave={handleSaveSteamProfileUrl}
                        saving={savingSteamProfileUrl}
                        wrapSx={ACCOUNT_PAGE_FIELD_INPUT_WRAP_WIDE_SX}
                    />

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountInfoField
                        id="account-steam-inventory"
                        label="Steam CS Inventory"
                        value={savedSteamInventoryDisplay}
                        editing={editingSteamInventory}
                        onEdit={() => {
                            resetEditors();
                            setDraftSteamInventoryValue(
                                savedSteamInventoryValue || "none",
                            );
                            setEditingSteamInventory(true);
                        }}
                        onCancel={() => {
                            setEditingSteamInventory(false);
                            setDraftSteamInventoryValue("");
                        }}
                        onSave={handleSaveSteamInventory}
                        saving={savingSteamInventory}
                        editor={
                            <SteamInventorySelect
                                value={draftSteamInventoryValue}
                                onChange={(nextValue) => {
                                    setDraftSteamInventoryValue(nextValue);
                                }}
                            />
                        }
                    />

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountInfoField
                        id="account-faceit-profile-url"
                        label="FACEIT profile URL (optional)"
                        labelAdornment={
                            <Tooltip
                                title="Your FACEIT profile lets Ouro import your current Elo for a more accurate starting rating."
                                placement="top"
                            >
                                <Box
                                    component="span"
                                    tabIndex={0}
                                    aria-label="Why Ouro asks for your FACEIT profile URL"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        color: colors.text.muted,
                                        cursor: "help",
                                        transition: "color 0.15s ease-in-out",
                                        "&:hover, &:focus-visible": {
                                            color: colors.text.secondary,
                                        },
                                        "&:focus-visible": {
                                            outline: `1px solid ${colors.primary.main}`,
                                            outlineOffset: "2px",
                                            borderRadius: "50%",
                                        },
                                    }}
                                >
                                    <InfoOutlined sx={{ fontSize: "1rem" }} />
                                </Box>
                            </Tooltip>
                        }
                        value={savedFaceitProfileUrl}
                        editable={canEditFaceitProfileUrl}
                        editing={editingFaceitProfileUrl}
                        draftValue={draftFaceitProfileUrl}
                        onDraftChange={setDraftFaceitProfileUrl}
                        onEdit={() => {
                            resetEditors();
                            setDraftFaceitProfileUrl(savedFaceitProfileUrl);
                            setEditingFaceitProfileUrl(true);
                        }}
                        onCancel={() => {
                            setEditingFaceitProfileUrl(false);
                            setDraftFaceitProfileUrl("");
                        }}
                        onSave={handleSaveFaceitProfileUrl}
                        saving={savingFaceitProfileUrl}
                        wrapSx={ACCOUNT_PAGE_FIELD_INPUT_WRAP_WIDE_SX}
                    />
                    <Collapse
                        in={
                            editingFaceitProfileUrl &&
                            Boolean(draftFaceitProfileUrl.trim())
                        }
                        timeout={180}
                    >
                        <Box sx={ACCOUNT_PAGE_MATCHMAKING_NOTICE_SX}>
                            <InfoRounded aria-hidden />
                            <Box component="span">
                                Saving a FACEIT profile URL will return your account
                                to pending review so admins can re-verify the new
                                profile details.
                            </Box>
                        </Box>
                    </Collapse>
                </AccountCollapsibleSubsection>

                <AccountCollapsibleSubsection
                    id="account-personal-details"
                    title="Personal details"
                    showDivider
                >
                    <Box sx={{ mt: "1.25rem" }}>
                        <AccountInfoField
                            id="account-legal-name"
                            label="Name"
                            value={legalName}
                            editing={editingName}
                            hideLabel={editingName}
                            onEdit={() => {
                                resetEditors();
                                setDraftGivenName(savedNameParts.givenName);
                                setDraftFamilyName(savedNameParts.familyName);
                                setEditingName(true);
                            }}
                            onCancel={() => {
                                setEditingName(false);
                                setDraftGivenName("");
                                setDraftFamilyName("");
                            }}
                            wrapSx={
                                editingName
                                    ? ACCOUNT_PAGE_FIELD_INPUT_WRAP_WIDE_SX
                                    : ACCOUNT_PAGE_FIELD_INPUT_WRAP_SX
                            }
                            onSave={handleSaveName}
                            saving={savingName}
                            editor={
                                <Box sx={ACCOUNT_PAGE_INLINE_FIELDS_SX}>
                                    <AccountLabeledInput
                                        id="account-given-name"
                                        label="Name"
                                        value={draftGivenName}
                                        autoFocus
                                        onChange={(event) => {
                                            setDraftGivenName(event.target.value);
                                        }}
                                    />
                                    <AccountLabeledInput
                                        id="account-family-name"
                                        label="Surname"
                                        value={draftFamilyName}
                                        onChange={(event) => {
                                            setDraftFamilyName(event.target.value);
                                        }}
                                    />
                                </Box>
                            }
                        />
                    </Box>

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountInfoField
                        id="account-date-of-birth"
                        label="Date of Birth"
                        value={dateOfBirth}
                        editing={editingDob}
                        onEdit={() => {
                            resetEditors();
                            setDraftDob(formatDateOfBirthInputValue(user?.dateOfBirth));
                            setEditingDob(true);
                        }}
                        onCancel={() => {
                            setEditingDob(false);
                            setDraftDob("");
                        }}
                        onSave={handleSaveDob}
                        saving={savingDob}
                        editor={
                            <TextField
                                id="account-date-of-birth"
                                variant="outlined"
                                value={draftDob}
                                onChange={(event) => {
                                    setDraftDob(maskDateOfBirthInput(event.target.value));
                                }}
                                autoFocus
                                placeholder="MM/DD/YYYY"
                                sx={OUTLINED_INPUT_SX}
                                slotProps={{
                                    htmlInput: {
                                        "aria-label": "Date of Birth",
                                        inputMode: "numeric",
                                        maxLength: 10,
                                        autoComplete: "bday",
                                        placeholder: "MM/DD/YYYY",
                                    },
                                }}
                            />
                        }
                    />

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountInfoField
                        id="account-gender"
                        label="Gender"
                        value={gender}
                        editing={editingGender}
                        onEdit={() => {
                            resetEditors();
                            setDraftGender(
                                user?.gender === "female" || user?.gender === "male"
                                    ? user.gender
                                    : "",
                            );
                            setEditingGender(true);
                        }}
                        onCancel={() => {
                            setEditingGender(false);
                            setDraftGender("");
                        }}
                        onSave={handleSaveGender}
                        saving={savingGender}
                        editor={
                            <TextField
                                id="account-gender"
                                select
                                value={draftGender}
                                onChange={(event) => {
                                    setDraftGender(event.target.value);
                                }}
                                placeholder="Select gender"
                                sx={OUTLINED_INPUT_SX}
                                slotProps={{
                                    select: {
                                        displayEmpty: true,
                                        IconComponent: KeyboardArrowDownRounded,
                                        MenuProps: {
                                            PaperProps: {
                                                sx: COUNTRY_SELECT_PAPER_SX,
                                            },
                                        },
                                        renderValue: (value) => {
                                            const selected = GENDER_OPTIONS.find(
                                                (option) => option.value === value,
                                            );
                                            return selected?.displayName || "Select gender";
                                        },
                                    },
                                    htmlInput: {
                                        "aria-label": "Gender",
                                    },
                                }}
                            >
                                {GENDER_OPTIONS.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                        sx={{
                                            ...COUNTRY_SELECT_LISTBOX_SX,
                                            fontSize: "0.95rem",
                                            color: colors.text.primary,
                                            "&.Mui-selected": {
                                                backgroundColor: `${alpha(colors.primary.main, 0.24)} !important`,
                                                color: colors.accent.main,
                                            },
                                            "&.Mui-selected:hover": {
                                                backgroundColor: `${alpha(colors.primary.main, 0.32)} !important`,
                                            },
                                        }}
                                    >
                                        {option.displayName}
                                        {draftGender === option.value ? (
                                            <Box
                                                component="span"
                                                aria-hidden
                                                sx={{
                                                    ml: "auto",
                                                    flexShrink: 0,
                                                    fontSize: "0.85rem",
                                                    fontWeight: 700,
                                                    color: colors.accent.soft,
                                                }}
                                            >
                                                ✓
                                            </Box>
                                        ) : null}
                                    </MenuItem>
                                ))}
                            </TextField>
                        }
                    />
                </AccountCollapsibleSubsection>

                <AccountCollapsibleSubsection
                    id="account-verification"
                    title="Verification"
                    showDivider
                >
                    <Box sx={{ mt: "1.25rem" }}>
                        <AccountPhoneField user={user} actions={actions} />
                    </Box>

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountTwoFactorRow
                        enabled={Boolean(user?.twoFactorEnabled)}
                        onEdit={() => {
                            onSectionChange?.("two-factor-auth");
                        }}
                    />

                    <Box sx={ACCOUNT_PAGE_FIELD_DIVIDER_SX} />

                    <AccountIdentityVerification />
                </AccountCollapsibleSubsection>

                <AccountCollapsibleSubsection
                    id="account-management"
                    title="Account management"
                >
                    <Box sx={{ mt: "1.25rem" }}>
                        <AccountDataDeletionSection user={user} embedded />
                    </Box>
                </AccountCollapsibleSubsection>
            </Box>
        </Box>
    );
}

AccountTabSection.propTypes = {
    user: PropTypes.object,
    actions: PropTypes.object,
    refreshToken: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
    ]),
    onSectionChange: PropTypes.func,
};

export default React.memo(AccountTabSection);
